import { useState } from 'react';
import { ReactComponent as ChevronRight } from '@assets/icons/chevron-right.svg';
import config from 'config';
import Image from 'next/image';

import { Box, Flex, Stack, Text, useBoolean, useTheme } from '@chakra-ui/react';

import { convertTimeToAMPMFormat } from 'utils';

import { AppointmentInterface } from 'types';

import { Loader } from 'ui';

const {
  booking: { DefaultProviderAvatar }
} = config;

interface AppointmentItemProps {
  item: AppointmentInterface;
  isFirstItem?: boolean;
  onClick: (id: AppointmentInterface) => void;
}

const AppointmentItem = ({
  item,
  isFirstItem,
  onClick
}: AppointmentItemProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepTime: {
            apptData: {
              list: { item: itemColors }
            }
          }
        }
      }
    }
  } = useTheme();
  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useBoolean(true);

  const { start_time, end_time, services } = item;

  const isMultipleServices = services.length > 1;
  const isTheSameProvider =
    !isMultipleServices ||
    services.every(
      (service) => service?.assigned_em?.[0] === services?.[0]?.assigned_em?.[0]
    );
  const firstService = services[0];

  const uniqueProviderNames = Array.from(
    new Set(services.map((serviceItem) => serviceItem?.em_name?.[0]))
  );
  const providersTitle = uniqueProviderNames.join(', ');
  const providerTitle = isTheSameProvider
    ? firstService?.em_name?.[0]
    : providersTitle;

  const handleClick = () => {
    onClick(item);
  };

  return (
    <Flex
      align='center'
      bg={itemColors.background}
      borderTopRadius={{ base: '6px', md: isFirstItem ? '0' : '6px' }}
      borderBottomRadius='6px'
      py='9px'
      pl='15px'
      pr='20px'
      onClick={handleClick}
      border='1px solid'
      borderColor='transparent'
      gap='10px'
      _hover={{
        borderColor: itemColors.hoverBorderColor,
        cursor: 'pointer'
      }}
    >
      <Stack gap='3px' mr={{ base: '10px', md: '20px', lg: '40px' }} w='90px'>
        <Text
          color={itemColors.timeTextColor}
          fontWeight='semibold'
          fontSize='16px'
          whiteSpace='nowrap'
        >
          {convertTimeToAMPMFormat(start_time)}
        </Text>
        <Text
          fontSize='12px'
          color={itemColors.endTimeTextColor}
          whiteSpace='nowrap'
        >
          {convertTimeToAMPMFormat(end_time)}
        </Text>
      </Stack>

      <Flex
        gap='10px'
        alignItems='center'
        justifyContent='space-between'
        w='100%'
      >
        <Flex gap='20px' alignItems='center'>
          {isTheSameProvider && !imageError ? (
            <Box
              w='40px'
              h='40px'
              borderRadius='50%'
              overflow='hidden'
              position='relative'
              flexShrink={0}
            >
              {loading && <Loader absolute />}
              <Image
                src={firstService?.public_photo_url?.[0] ?? ''}
                alt='Appointment provider'
                width={40}
                height={40}
                sizes='(max-width: 450px) 40px, 65px'
                onError={() => setImageError(true)}
                onLoad={setLoading.off}
              />
            </Box>
          ) : (
            <Flex
              w='40px'
              h='40px'
              bg={itemColors.multipleProvidersBg}
              alignItems='center'
              justifyContent='center'
              borderRadius='50%'
              flexShrink={0}
              opacity={0.7}
            >
              <DefaultProviderAvatar
                w='40px'
                h='40px'
                fill={itemColors.multipleProvidersIconColor}
              />
            </Flex>
          )}

          <Box flex={1}>
            <Text
              color={itemColors.providerNameTextColor}
              fontSize='16px'
              fontWeight='semibold'
              noOfLines={1}
            >
              {providerTitle}
            </Text>
          </Box>
        </Flex>

        <ChevronRight
          width={8}
          height={16}
          fill={itemColors.chevronIconColor}
          style={{ flexShrink: 0 }}
        />
      </Flex>
    </Flex>
  );
};

export default AppointmentItem;
